// Organisations-Bereich (NT-ORG-01 bis NT-ORG-04, Struktur seit NT-ORG-UI-01).
//
// NT-ORG-UI-01: Der Bereich hat Unter-Tabs (ÜBERSICHT, AUFTRÄGE, KASSE, HIERARCHIE, LAGER, GELÄNDE [NT-ORG-08], AUSBAU) plus
// das Verzeichnis ORGANISATIONEN. Jeder Tab lädt NUR seine eigenen Daten über einen eigenen
// Endpunkt (T2) — /orgs/me liefert nur die Übersicht und die Info, ob man Mitglied ist. Ohne Org
// landet man direkt im Verzeichnis (V1). Der zuletzt gewählte Unter-Tab bleibt erhalten (T1,
// Muster: crimeops_section in crimeOps.ts).
//
// Alle Markup-Bausteine leben DOM-frei in org.render.ts (testbar), hier liegen Zustand, Laden,
// Event-Listener und der Lager-Dialog.
//
// NT-ORG-03-Lager-Dialog: dünner Wrapper um den bestehenden Transport-Flow (`/orgs/lager/deposit`/
// `withdraw` rufen serverseitig startTransport() auf), deshalb EIN Item pro Vorgang.

import { api, ApiError } from '../services/api.js';
import { navigate } from '../router.js';
import { gameState } from '../state/gameState.js';
import { showToast } from '../utils/toast.js';
import { formatItemKey } from '../utils/formatters.js';
import { renderPropertyOptions, type PropertySelectItem } from '../utils/property-select.js';
import {
  NO_AUTOFILL,
  ORG_RECHTE,
  esc,
  renderAuftraegeAnsichtSwitch,
  renderAuftraegeTab,
  renderProduktionTab,
  renderAusbauTab,
  renderGelaendeTab,
  renderDirectoryList,
  renderHierarchieTab,
  renderKasseTab,
  renderLagerTab,
  renderLeaderboard,
  renderNoOrgPanel,
  renderOverviewTab,
  renderSubTabs,
  type OrgAuftraegeAnsicht,
  type OrgAuftraegeTabView,
  type OrgProdEbene,
  type OrgProduktionTabView,
  type OrgVerkaufKanal,
  type OrgAusbauTabView,
  type OrgGelaendeTabView,
  type OrgDirectoryFilter,
  type OrgDirectoryResult,
  type OrgHierarchieTabView,
  type OrgKasseTabView,
  type OrgLagerTabView,
  type OrgLeaderboardResult,
  type OrgLeaderboardSort,
  type OrgLogEntryView,
  type OrgPageData,
  type OrgSubTab,
} from './org.render.js';

// ─── Lager-Dialog-Hilfstypen (gespiegelt von lagerhaus.ts, nur benötigte Felder) ───────────────

interface OrgLagerDialogProperty {
  id: string;
  subType: string;
  label?: string;
  customName: string | null;
  nickname: string | null;
  slots: number;
  currentLoad: number;
  inventoryItems: { itemType: string; itemKey: string; quantity: number }[];
  /** Nur clientseitig gesetzt (getOwnProperties) — für die LAGER/VERSTECKE-Gruppierung. */
  isHideout?: boolean;
}
interface OrgLagerDialogVehicle {
  vehicleKey: string;
  quantity: number;
  dealerAssignedCount: number;
  propertyAssignedCount: number;
  busyCount: number;
}
interface OrgLagerPropertiesData {
  warehouses: OrgLagerDialogProperty[];
  hideouts: OrgLagerDialogProperty[];
  garage: { vehicles: OrgLagerDialogVehicle[]; baseVehicles: OrgLagerDialogVehicle[] };
}

type LagerDialogMode = 'DEPOSIT' | 'WITHDRAW' | 'REQUEST';
interface LagerDialogState {
  mode: LagerDialogMode;
  propertyId: string | null; // Quelle (DEPOSIT) bzw. Ziel (WITHDRAW) — unbenutzt bei REQUEST
  itemKey: string | null;
  itemType: string | null;
  availableQty: number;
  quantity: number;
  vehicleKey: string | null; // unbenutzt bei REQUEST
  begruendung: string; // nur REQUEST
  isSubmitting: boolean;
  error: string | null;
}

const SUBTAB_STORAGE_KEY = 'org_subtab';
const OVERVIEW_LOG_LIMIT = 10;
const VALID_SUBTABS: readonly OrgSubTab[] = ['UEBERSICHT', 'AUFTRAEGE', 'KASSE', 'HIERARCHIE', 'LAGER', 'GELAENDE', 'AUSBAU', 'VERZEICHNIS'];

// ─── State ───────────────────────────────────────────────────────────────────────

let pageData: OrgPageData | null = null;
let activeTab: OrgSubTab = 'UEBERSICHT';
let isLoading = false;

// Je Tab nur die eigenen Daten (T2) — null = noch nicht geladen.
let overviewLog: OrgLogEntryView[] | null = null;
let kasseTab: OrgKasseTabView | null = null;
let hierarchieTab: OrgHierarchieTabView | null = null;
let lagerTab: OrgLagerTabView | null = null;
let ausbauTab: OrgAusbauTabView | null = null;
/** NT-ORG-08 — Tab GELÄNDE und seine aufgeklappten Bereiche (nur Anzeige). */
let gelaendeTab: OrgGelaendeTabView | null = null;
let offeneGebaeude = new Set<string>();
let bauParzelleId: string | null = null;
let anlageGebaeudeId: string | null = null;
let auftraegeTab: OrgAuftraegeTabView | null = null;
/** Aufgeklappte Kettenstufen und das offene Übernehmen-Formular (NT-ORG-07a). */
let expandedStufen = new Set<number>();
let offenesAufgabeFormular: string | null = null;
/** NT-ORG-07b — zweite Ansicht im Tab AUFTRÄGE, zugeklappte Pyramiden-Ebenen (mobil). */
let auftraegeAnsicht: OrgAuftraegeAnsicht = 'KETTE';
let produktionTab: OrgProduktionTabView | null = null;
let collapsedEbenen = new Set<OrgProdEbene>();
let directory: OrgDirectoryResult | null = null;
let leaderboard: OrgLeaderboardResult | null = null;

let directoryFilter: OrgDirectoryFilter = { search: '', sort: 'members', freiePlaetze: false };
let directoryPage = 1;
let leaderboardSort: OrgLeaderboardSort = 'stufe';
let leaderboardPage = 1;
let applyOpenOrgId: string | null = null;

// NT-ORG-FIX-01 (U3) — je Zweig einzeln aufklappbar (Mobile: keine horizontale Scrollbar).
let expandedZweige = new Set<string>();

// NT-ORG-03 — lazy beim ersten Öffnen eines Lager-Dialogs geladen, bis Tab-Verlassen gecacht.
let lagerProps: OrgLagerPropertiesData | null = null;
let lagerDialog: LagerDialogState | null = null;

// ─── Hilfsfunktionen ──────────────────────────────────────────────────────────────

function errorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    // Zod-Feldfehler (Prompt-Muster aus SchwarzesBrettView.ts) haben Vorrang vor dem Sammeltext.
    const issues = (err.details as { issues?: { message?: unknown }[] } | undefined)?.issues;
    const first = issues?.[0]?.message;
    if (typeof first === 'string') return first;
    return err.message;
  }
  return 'Etwas ist schiefgelaufen.';
}

/** "123,45" oder "123.45" → ganze Cent (>=1), null bei ungültiger Eingabe. */
function parseAmountToCents(raw: string): number | null {
  const normalized = raw.trim().replace(',', '.');
  if (normalized.length === 0) return null;
  const value = Number(normalized);
  if (!Number.isFinite(value) || value <= 0) return null;
  return Math.round(value * 100);
}

function getMyPlayerId(): string {
  return gameState.getPlayer()?.id ?? '';
}

function readStoredTab(): OrgSubTab {
  try {
    const stored = sessionStorage.getItem(SUBTAB_STORAGE_KEY);
    return VALID_SUBTABS.find((t) => t === stored) ?? 'UEBERSICHT';
  } catch {
    return 'UEBERSICHT';
  }
}

function storeTab(tab: OrgSubTab): void {
  try {
    sessionStorage.setItem(SUBTAB_STORAGE_KEY, tab);
  } catch {
    // privater Modus o.ä. — Tab merkt sich dann nur nicht, kein Fehler
  }
}

function fieldValue(form: HTMLFormElement, name: string): string {
  const el = form.elements.namedItem(name);
  return el instanceof HTMLInputElement || el instanceof HTMLSelectElement || el instanceof HTMLTextAreaElement ? el.value : '';
}

// ─── Lifecycle (Dashboard-Tab) ────────────────────────────────────────────────────

export function renderOrgTab(): string {
  return `
    <section class="dash-content" id="dash-org">
      <div class="org-tab-header">
        <h2>ORGANISATION <button type="button" class="org-help-btn" data-action="help-org" title="Was ist eine Organisation? Was kostet das?">?</button></h2>
      </div>
      <div id="org-content">
        <p class="loading-text">ORGANISATION WIRD GELADEN...</p>
      </div>
    </section>
  `;
}

export function mountOrgTab(): void {
  const player = gameState.getPlayer();
  if (player === null) { navigate('/login'); return; }

  activeTab = readStoredTab();
  attachListeners();
  void loadAndRender();
}

export function cleanupOrgTab(): void {
  pageData = null;
  isLoading = false;
  overviewLog = null;
  kasseTab = null;
  hierarchieTab = null;
  lagerTab = null;
  ausbauTab = null;
  gelaendeTab = null;
  offeneGebaeude = new Set<string>();
  bauParzelleId = null;
  anlageGebaeudeId = null;
  auftraegeTab = null;
  expandedStufen = new Set<number>();
  offenesAufgabeFormular = null;
  auftraegeAnsicht = 'KETTE';
  produktionTab = null;
  collapsedEbenen = new Set<OrgProdEbene>();
  directory = null;
  leaderboard = null;
  applyOpenOrgId = null;
  expandedZweige = new Set<string>();
  lagerProps = null;
  lagerDialog = null;
}

/** Lädt die Übersicht (Mitgliedschaft) und danach ausschließlich die Daten des aktiven Tabs. */
async function loadAndRender(): Promise<void> {
  if (isLoading) return;
  isLoading = true;
  try {
    pageData = await api.get<OrgPageData>('/orgs/me');
    // V1 — ohne Org gibt es nur das Verzeichnis.
    if (!pageData.org) activeTab = 'VERZEICHNIS';
    await loadTabData(activeTab);
  } catch {
    const content = document.getElementById('org-content');
    if (content) content.innerHTML = '<p class="error-text">ORGANISATION KONNTE NICHT GELADEN WERDEN.</p>';
    isLoading = false;
    return;
  }
  isLoading = false;
  renderContent();
}

/** T2 — jeder Tab ruft nur seinen eigenen Endpunkt auf. */
async function loadTabData(tab: OrgSubTab): Promise<void> {
  switch (tab) {
    case 'UEBERSICHT':
      overviewLog = (await api.get<OrgLogEntryView[]>('/orgs/log')).slice(0, OVERVIEW_LOG_LIMIT);
      return;
    case 'KASSE':
      kasseTab = await api.get<OrgKasseTabView>('/orgs/kasse');
      return;
    case 'HIERARCHIE':
      hierarchieTab = await api.get<OrgHierarchieTabView>('/orgs/hierarchie');
      return;
    case 'LAGER':
      lagerTab = await api.get<OrgLagerTabView>('/orgs/lager');
      return;
    case 'AUFTRAEGE':
      [auftraegeTab, produktionTab] = await Promise.all([
        api.get<OrgAuftraegeTabView>('/orgs/auftraege'),
        api.get<OrgProduktionTabView>('/orgs/produktion'),
      ]);
      // Läuft nur eine Produktion, gleich dorthin (sonst bleibt die zuletzt gewählte Ansicht).
      if (!auftraegeTab.laufend && produktionTab.laufend && auftraegeAnsicht === 'KETTE') auftraegeAnsicht = 'PRODUKTION';
      // Beim ersten Laden die eigene Stufe aufklappen, damit man sofort sieht, was zu tun ist.
      if (expandedStufen.size === 0 && auftraegeTab.meineStufe !== null) expandedStufen.add(auftraegeTab.meineStufe);
      return;
    case 'GELAENDE':
      // Fahrzeuge für die Ware-Transporte der Gebäude mitladen (gleiche Quelle wie der Lager-Dialog).
      [gelaendeTab] = await Promise.all([api.get<OrgGelaendeTabView>('/orgs/gelaende'), ensureLagerPropsLoaded()]);
      return;
    case 'AUSBAU':
      ausbauTab = await api.get<OrgAusbauTabView>('/orgs/ausbau');
      return;
    case 'VERZEICHNIS':
      await loadDirectory();
      return;
  }
}

async function loadDirectory(): Promise<void> {
  const params = new URLSearchParams({
    sort: directoryFilter.sort,
    freiePlaetze: String(directoryFilter.freiePlaetze),
    page: String(directoryPage),
  });
  if (directoryFilter.search.length > 0) params.set('search', directoryFilter.search);
  const leaderboardParams = new URLSearchParams({ sort: leaderboardSort, page: String(leaderboardPage) });
  [directory, leaderboard] = await Promise.all([
    api.get<OrgDirectoryResult>(`/orgs/directory?${params.toString()}`),
    api.get<OrgLeaderboardResult>(`/orgs/leaderboard?${leaderboardParams.toString()}`),
  ]);
}

async function switchTab(tab: OrgSubTab): Promise<void> {
  if (tab === activeTab) return;
  activeTab = tab;
  storeTab(tab);
  lagerDialog = null;
  const content = document.getElementById('org-content');
  if (content) content.innerHTML = `${renderSubTabs(activeTab)}<p class="loading-text">WIRD GELADEN...</p>`;
  try {
    await loadTabData(tab);
  } catch (err) {
    showToast(errorMessage(err), 'error');
  }
  renderContent();
}

// ─── Rendering ─────────────────────────────────────────────────────────────────────

function renderContent(): void {
  const content = document.getElementById('org-content');
  if (!content || !pageData) return;

  if (!pageData.org) {
    content.innerHTML = renderNoOrgPanel(pageData) + renderDirectorySection();
    return;
  }
  content.innerHTML = renderSubTabs(activeTab) + renderActiveTab() + renderLagerDialog();
}

function renderDirectorySection(): string {
  if (!directory || !leaderboard) return '<p class="loading-text">WIRD GELADEN...</p>';
  return renderDirectoryList(directory, directoryFilter, applyOpenOrgId) + renderLeaderboard(leaderboard, leaderboardSort);
}

function renderActiveTab(): string {
  const org = pageData?.org;
  if (!org) return '';
  const loading = '<p class="loading-text">WIRD GELADEN...</p>';
  switch (activeTab) {
    case 'UEBERSICHT':
      return overviewLog ? renderOverviewTab(org, overviewLog) : loading;
    case 'KASSE':
      return kasseTab ? renderKasseTab(kasseTab) : loading;
    case 'HIERARCHIE':
      return hierarchieTab ? renderHierarchieTab(hierarchieTab, getMyPlayerId()) : loading;
    case 'LAGER':
      return lagerTab ? renderLagerTab(lagerTab, formatItemKey) : loading;
    case 'AUFTRAEGE': {
      if (!auftraegeTab || !produktionTab) return loading;
      const inhalt = auftraegeAnsicht === 'KETTE'
        ? renderAuftraegeTab(auftraegeTab, expandedStufen, offenesAufgabeFormular)
        : renderProduktionTab(produktionTab, collapsedEbenen);
      return renderAuftraegeAnsichtSwitch(auftraegeAnsicht) + inhalt;
    }
    case 'GELAENDE':
      return gelaendeTab
        ? renderGelaendeTab(
          gelaendeTab,
          { offeneGebaeude, bauParzelleId, anlageGebaeudeId },
          getAvailableVehicles().map((v) => ({ vehicleKey: v.vehicleKey, label: `${formatItemKey(v.vehicleKey)} (${String(v.available)})` })),
          formatItemKey,
        )
        : loading;
    case 'AUSBAU':
      return ausbauTab ? renderAusbauTab(ausbauTab, expandedZweige) : loading;
    case 'VERZEICHNIS':
      return renderDirectorySection();
  }
}

// ─── Lager-Dialog (NT-ORG-03) ───────────────────────────────────────────────────────

function renderLagerQtyControl(d: LagerDialogState): string {
  // Kein Stückzahl-Deckel — die Slot-Kapazität des Fahrzeugs prüft der Server.
  const max = d.availableQty;
  return `
    <label class="org-form-label">Menge (verfügbar: ${String(d.availableQty)})</label>
    <div class="org-qty-ctrl">
      <button type="button" class="org-qty-btn" data-action="lager-qty-dec" ${d.quantity <= 1 ? 'disabled' : ''}>&#8722;</button>
      <input type="number" class="org-qty-input" id="org-lager-qty-input" min="1" max="${String(max)}"
        value="${String(d.quantity)}" data-org-qty-max="${String(max)}" ${NO_AUTOFILL} />
      <button type="button" class="org-qty-btn" data-action="lager-qty-inc" ${d.quantity >= max ? 'disabled' : ''}>&#43;</button>
      <button type="button" class="org-qty-btn org-qty-btn--max" data-action="lager-qty-max">MAX</button>
    </div>
  `;
}

/** Patcht Eingabefeld/Buttons nach direkter Mengen-Eingabe, ohne renderContent() (Muster: markt.ts::updateQtyDisplay). */
function updateLagerQtyDisplay(): void {
  if (!lagerDialog) return;
  const d = lagerDialog;
  const max = d.availableQty;

  const input = document.getElementById('org-lager-qty-input') as HTMLInputElement | null;
  if (input) input.value = String(d.quantity);
  document.querySelector<HTMLButtonElement>('[data-action="lager-qty-dec"]')?.toggleAttribute('disabled', d.quantity <= 1);
  document.querySelector<HTMLButtonElement>('[data-action="lager-qty-inc"]')?.toggleAttribute('disabled', d.quantity >= max);

  const confirmAction = d.mode === 'REQUEST' ? 'confirm-lager-request' : d.mode === 'DEPOSIT' ? 'confirm-lager-deposit' : 'confirm-lager-withdraw';
  const confirmBtn = document.querySelector<HTMLButtonElement>(`[data-action="${confirmAction}"]`);
  if (confirmBtn) {
    confirmBtn.disabled = d.mode === 'REQUEST'
      ? !d.itemKey || d.quantity < 1 || d.begruendung.trim().length === 0 || d.isSubmitting
      : !d.propertyId || !d.itemKey || !d.vehicleKey || d.quantity < 1 || d.isSubmitting;
  }
}

function renderLagerDialog(): string {
  if (!lagerDialog || !lagerTab) return '';
  const d = lagerDialog;
  const lager = lagerTab.lager;

  if (d.mode === 'REQUEST') {
    const disabled = !d.itemKey || d.quantity < 1 || d.begruendung.trim().length === 0 || d.isSubmitting;
    return `
      <div class="modal-backdrop" id="org-lager-dialog">
        <div class="modal modal--narrow">
          <div class="modal-header"><h2>WARE ANFORDERN</h2><p class="modal-subtitle">Reine Koordination — keine automatische Entnahme.</p></div>
          <div class="modal-body">
            <label class="org-form-label">Ware</label>
            <select id="org-lager-item" class="org-select">
              ${lager.inventory.map((i) => `<option value="${esc(i.itemKey)}|${esc(i.itemType)}" ${i.itemKey === d.itemKey ? 'selected' : ''}>${esc(formatItemKey(i.itemKey))} (${String(i.quantity)})</option>`).join('')}
            </select>
            ${renderLagerQtyControl(d)}
            <label class="org-form-label">Begründung</label>
            <textarea id="org-lager-begruendung" class="org-select" maxlength="200" rows="3" ${NO_AUTOFILL}>${esc(d.begruendung)}</textarea>
            ${d.error ? `<p class="org-lager-error">${esc(d.error)}</p>` : ''}
          </div>
          <div class="modal-actions">
            <button class="btn-org btn-org--accent" data-action="confirm-lager-request" ${disabled ? 'disabled' : ''}>ANFRAGE SENDEN</button>
            <button class="btn-org" data-action="close-lager-dialog">ABBRECHEN</button>
          </div>
        </div>
      </div>
    `;
  }

  const isDeposit = d.mode === 'DEPOSIT';
  const ownProps = getOwnProperties();
  const vehicles = getAvailableVehicles();
  const propsForSelect = isDeposit
    ? ownProps.filter((p) => p.inventoryItems.some((i) => i.itemType !== 'VEHICLE' && i.quantity > 0))
    : ownProps;
  const itemsForSelect = isDeposit
    ? (ownProps.find((p) => p.id === d.propertyId)?.inventoryItems.filter((i) => i.itemType !== 'VEHICLE' && i.quantity > 0) ?? [])
    : lager.inventory;
  const confirmDisabled = !d.propertyId || !d.itemKey || !d.vehicleKey || d.quantity < 1 || d.isSubmitting;

  const noPropsHint = propsForSelect.length === 0
    ? `<p class="org-lager-error">${isDeposit ? 'Kein eigenes Lager/Versteck mit Ware zum Einlagern vorhanden.' : 'Kein eigenes Lager/Versteck vorhanden.'}</p>`
    : '';
  const noVehicleHint = vehicles.length === 0 ? '<p class="org-lager-error">Kein freies Fahrzeug in der Garage.</p>' : '';

  return `
    <div class="modal-backdrop" id="org-lager-dialog">
      <div class="modal modal--narrow">
        <div class="modal-header"><h2>${isDeposit ? 'INS ORG-LAGER EINLAGERN' : 'AUS DEM ORG-LAGER ENTNEHMEN'}</h2></div>
        <div class="modal-body">
          ${!isDeposit && lager.entnahmeRemainingSlots !== null ? `<p class="org-hint">Noch ${String(lager.entnahmeRemainingSlots)} Slots heute entnehmbar.</p>` : ''}
          ${noPropsHint}
          <label class="org-form-label">${isDeposit ? 'Quelle' : 'Ziel'}</label>
          <select id="org-lager-property" class="org-select">
            ${renderPropertyOptions(propsForSelect.map((p) => toPropertySelectItem(p, !isDeposit)), { selectedId: d.propertyId })}
          </select>
          <label class="org-form-label">Ware</label>
          <select id="org-lager-item" class="org-select">
            ${itemsForSelect.map((i) => `<option value="${esc(i.itemKey)}|${esc(i.itemType)}" ${i.itemKey === d.itemKey ? 'selected' : ''}>${esc(formatItemKey(i.itemKey))} (${String(i.quantity)})</option>`).join('')}
          </select>
          ${renderLagerQtyControl(d)}
          ${noVehicleHint}
          <label class="org-form-label">Fahrzeug</label>
          <select id="org-lager-vehicle" class="org-select">
            ${vehicles.map((v) => `<option value="${esc(v.vehicleKey)}" ${v.vehicleKey === d.vehicleKey ? 'selected' : ''}>${esc(formatItemKey(v.vehicleKey))} (${String(v.available)})</option>`).join('')}
          </select>
          ${d.error ? `<p class="org-lager-error">${esc(d.error)}</p>` : ''}
        </div>
        <div class="modal-actions">
          <button class="btn-org btn-org--accent" data-action="${isDeposit ? 'confirm-lager-deposit' : 'confirm-lager-withdraw'}" ${confirmDisabled ? 'disabled' : ''}>${isDeposit ? 'EINLAGERN' : 'ENTNEHMEN'}</button>
          <button class="btn-org" data-action="close-lager-dialog">ABBRECHEN</button>
        </div>
      </div>
    </div>
  `;
}

function getOwnProperties(): OrgLagerDialogProperty[] {
  if (!lagerProps) return [];
  return [
    ...lagerProps.warehouses.map((p) => ({ ...p, isHideout: false })),
    ...lagerProps.hideouts.map((p) => ({ ...p, isHideout: true })),
  ];
}

/** NT-POLISH-01 (P1): Übergabe an die zentrale Immobilien-Auswahl. */
function toPropertySelectItem(p: OrgLagerDialogProperty, withFree: boolean): PropertySelectItem {
  return {
    id: p.id, label: p.label ?? null, subType: p.subType, customName: p.customName, nickname: p.nickname,
    isHideout: p.isHideout === true, freeSlots: withFree ? p.slots - p.currentLoad : null,
  };
}

// Verfügbarkeit exakt wie lagerhaus.ts (VERSENDEN-Modal).
function getAvailableVehicles(): { vehicleKey: string; available: number }[] {
  if (!lagerProps) return [];
  const all = [...lagerProps.garage.vehicles, ...lagerProps.garage.baseVehicles];
  return all
    .map((v) => ({ vehicleKey: v.vehicleKey, available: v.quantity - v.dealerAssignedCount - v.propertyAssignedCount - v.busyCount }))
    .filter((v) => v.available > 0);
}

async function ensureLagerPropsLoaded(): Promise<void> {
  if (lagerProps) return;
  lagerProps = await api.get<OrgLagerPropertiesData>('/properties');
}

function newDialog(mode: LagerDialogMode, item: { itemKey: string; itemType: string; quantity: number } | null, propertyId: string | null, vehicleKey: string | null): LagerDialogState {
  return {
    mode,
    propertyId,
    itemKey: item?.itemKey ?? null,
    itemType: item?.itemType ?? null,
    availableQty: item?.quantity ?? 0,
    quantity: item ? 1 : 0,
    vehicleKey,
    begruendung: '',
    isSubmitting: false,
    error: null,
  };
}

async function openLagerDepositDialog(): Promise<void> {
  await ensureLagerPropsLoaded();
  const props = getOwnProperties().filter((p) => p.inventoryItems.some((i) => i.itemType !== 'VEHICLE' && i.quantity > 0));
  const firstProp = props[0] ?? null;
  const firstItem = firstProp?.inventoryItems.find((i) => i.itemType !== 'VEHICLE' && i.quantity > 0) ?? null;
  lagerDialog = newDialog('DEPOSIT', firstItem, firstProp?.id ?? null, getAvailableVehicles()[0]?.vehicleKey ?? null);
  renderContent();
}

async function openLagerWithdrawDialog(): Promise<void> {
  if (!lagerTab?.lager.canWithdraw) return;
  await ensureLagerPropsLoaded();
  const firstItem = lagerTab.lager.inventory[0] ?? null;
  lagerDialog = newDialog('WITHDRAW', firstItem, getOwnProperties()[0]?.id ?? null, getAvailableVehicles()[0]?.vehicleKey ?? null);
  renderContent();
}

function openLagerRequestDialog(): void {
  if (!lagerTab) return;
  lagerDialog = newDialog('REQUEST', lagerTab.lager.inventory[0] ?? null, null, null);
  renderContent();
}

function handleLagerPropertyChange(propertyId: string): void {
  if (!lagerDialog) return;
  if (lagerDialog.mode === 'DEPOSIT') {
    const prop = getOwnProperties().find((p) => p.id === propertyId) ?? null;
    const firstItem = prop?.inventoryItems.find((i) => i.itemType !== 'VEHICLE' && i.quantity > 0) ?? null;
    lagerDialog = { ...newDialog('DEPOSIT', firstItem, propertyId, lagerDialog.vehicleKey) };
  } else {
    // WITHDRAW: Ziel-Wechsel betrifft nur den Transport-Endpunkt, die Item-Liste bleibt der Org-Bestand.
    lagerDialog = { ...lagerDialog, propertyId };
  }
  renderContent();
}

function handleLagerItemChange(value: string): void {
  if (!lagerDialog) return;
  const [itemKey, itemType] = value.split('|');
  if (!itemKey || !itemType) return;
  const availableQty = lagerDialog.mode === 'DEPOSIT'
    ? getOwnProperties().find((p) => p.id === lagerDialog?.propertyId)?.inventoryItems.find((i) => i.itemKey === itemKey)?.quantity ?? 0
    : lagerTab?.lager.inventory.find((i) => i.itemKey === itemKey)?.quantity ?? 0;
  lagerDialog = {
    ...lagerDialog,
    itemKey,
    itemType,
    availableQty,
    quantity: availableQty > 0 ? Math.min(lagerDialog.quantity, availableQty) || 1 : 0,
  };
  renderContent();
}

async function submitLagerDialog(): Promise<void> {
  const d = lagerDialog;
  if (!d?.itemKey || !d.itemType || d.quantity < 1) return;
  let request: () => Promise<unknown>;
  let successMessage: string;
  if (d.mode === 'REQUEST') {
    if (d.begruendung.trim().length === 0) return;
    request = () => api.post('/orgs/lager/requests', { itemKey: d.itemKey, itemType: d.itemType, quantity: d.quantity, begruendung: d.begruendung.trim() });
    successMessage = 'Anfrage gesendet.';
  } else {
    if (!d.propertyId || !d.vehicleKey) return;
    const isDeposit = d.mode === 'DEPOSIT';
    request = () => api.post(isDeposit ? '/orgs/lager/deposit' : '/orgs/lager/withdraw', {
      ...(isDeposit ? { fromPropertyId: d.propertyId } : { toPropertyId: d.propertyId }),
      vehicleKey: d.vehicleKey, itemKey: d.itemKey, itemType: d.itemType, quantity: d.quantity,
    });
    successMessage = isDeposit ? 'Transport gestartet — Ware ist unterwegs ins Org-Lager.' : 'Transport gestartet — Ware ist unterwegs zu dir.';
  }

  lagerDialog = { ...d, isSubmitting: true, error: null };
  renderContent();
  try {
    await request();
    lagerDialog = null;
    if (d.mode !== 'REQUEST') lagerProps = null; // Bestand hat sich geändert — beim nächsten Öffnen neu laden
    showToast(successMessage, 'info');
    await reloadActiveTab();
  } catch (err) {
    lagerDialog = { ...d, isSubmitting: false, error: errorMessage(err) };
    renderContent();
  }
}

// ─── Event-Listener ────────────────────────────────────────────────────────────────

function attachListeners(): void {
  const root = document.getElementById('dash-org');
  if (!root) return;
  root.addEventListener('click', handleClick);
  root.addEventListener('submit', handleSubmit);
  root.addEventListener('change', handleChange);
  // Nur die Anfrage-Begründung im Lager-Dialog: kein Re-Render pro Tastendruck (Fokus bleibt).
  root.addEventListener('input', handleInput);
}

function handleClick(e: Event): void {
  const target = e.target as HTMLElement;
  const btn = target.closest<HTMLButtonElement>('button[data-action]');
  if (!btn) return;

  switch (btn.dataset.action) {
    case 'help-org':
      navigate('/kladde?view=spickzettel&entry=die_organisation');
      return;
    case 'org-tab': {
      const tab = VALID_SUBTABS.find((t) => t === btn.dataset.orgTab);
      if (tab) void switchTab(tab);
      return;
    }
    case 'toggle-zweig': {
      const zweig = btn.dataset.zweig ?? '';
      if (expandedZweige.has(zweig)) { expandedZweige.delete(zweig); } else { expandedZweige.add(zweig); }
      renderContent();
      return;
    }
    case 'org-toggle-stufe': {
      const stufe = Number(btn.dataset.stufe ?? '0');
      if (expandedStufen.has(stufe)) { expandedStufen.delete(stufe); } else { expandedStufen.add(stufe); }
      renderContent();
      return;
    }
    case 'org-auftrag-starten': {
      const vorlageKey = btn.dataset.vorlage ?? '';
      if (!window.confirm('Auftrag starten? Der Einsatz wird sofort aus der Org-Kasse genommen.')) return;
      void runAction(() => api.post('/orgs/auftraege', { vorlageKey }), 'Auftrag gestartet.');
      return;
    }
    case 'org-auftraege-ansicht':
      auftraegeAnsicht = btn.dataset.ansicht === 'PRODUKTION' ? 'PRODUKTION' : 'KETTE';
      renderContent();
      return;
    case 'org-prod-toggle': {
      const ebene = btn.dataset.ebene as OrgProdEbene | undefined;
      if (!ebene) return;
      if (collapsedEbenen.has(ebene)) { collapsedEbenen.delete(ebene); } else { collapsedEbenen.add(ebene); }
      renderContent();
      return;
    }
    case 'org-prod-starten': {
      const angebotKey = btn.dataset.angebot ?? '';
      const select = document.getElementById(`org-prod-laufzeit-${angebotKey}`) as HTMLSelectElement | null;
      const laufzeitTage = Number(select?.value ?? '0');
      if (!window.confirm('Produktion starten? Der Einsatz wird sofort aus der Org-Kasse genommen.')) return;
      void runAction(() => api.post('/orgs/produktion', { angebotKey, laufzeitTage }), 'Produktion gestartet.');
      return;
    }
    case 'org-prod-abbrechen':
      if (!window.confirm('Produktion abbrechen? Gezahlte Löhne und gelieferte Zutaten bleiben, der Rest des Einsatzes geht zurück in die Kasse.')) return;
      void runAction(() => api.post('/orgs/produktion/abbrechen', {}), 'Produktion abgebrochen.');
      return;
    case 'org-job-besorgen':
      void runAction(() => api.post('/orgs/produktion/jobs', { typ: 'BESORGUNG', itemKey: btn.dataset.item ?? '' }), 'Besorgung läuft.');
      return;
    case 'org-job-verarbeiten':
      void runAction(() => api.post('/orgs/produktion/jobs', { typ: 'VERARBEITUNG' }), 'Verarbeitung läuft.');
      return;
    case 'org-auftrag-abbrechen':
      if (!window.confirm('Auftrag abbrechen? Bereits gezahlte Löhne bleiben, der Rest geht zurück in die Kasse.')) return;
      void runAction(() => api.post('/orgs/auftraege/abbrechen', {}), 'Auftrag abgebrochen.');
      return;
    case 'org-aufgabe-oeffnen':
      offenesAufgabeFormular = btn.dataset.aufgabeId ?? null;
      renderContent();
      return;
    case 'org-aufgabe-abbrechen':
      offenesAufgabeFormular = null;
      renderContent();
      return;
    case 'org-aufgabe-zurueckgeben': {
      const aufgabeId = btn.dataset.aufgabeId ?? '';
      if (!window.confirm('Aufgabe zurückgeben? Die Kiste verschwindet, es gibt keinen Lohn.')) return;
      void runAction(() => api.post(`/orgs/auftraege/aufgaben/${aufgabeId}/zurueckgeben`, {}), 'Aufgabe zurückgegeben.');
      return;
    }
    case 'accept-invite':
      void runAction(() => api.post(`/orgs/invites/${btn.dataset.inviteId ?? ''}/accept`, {}), 'Beigetreten.', { membershipChanged: true });
      return;
    case 'decline-invite':
      void runAction(() => api.post(`/orgs/invites/${btn.dataset.inviteId ?? ''}/decline`, {}), 'Einladung abgelehnt.');
      return;
    case 'leave-org': {
      const isSolo = pageData?.org?.memberCount === 1;
      const confirmText = isSolo
        ? 'Du bist das einzige Mitglied — das löst die Organisation sofort auf, das Restguthaben der Kasse wird dir zurückgezahlt. Fortfahren?'
        : 'Organisation wirklich verlassen?';
      if (!confirm(confirmText)) return;
      void runAction(() => api.post('/orgs/leave', {}), isSolo ? 'Organisation sofort aufgelöst.' : 'Organisation verlassen.', { membershipChanged: true });
      return;
    }
    case 'kick-member':
      if (!confirm('Dieses Mitglied wirklich rauswerfen?')) return;
      void runAction(() => api.post(`/orgs/members/${btn.dataset.playerId ?? ''}/kick`, {}), 'Mitglied entfernt.');
      return;
    case 'veto-dissolution':
      void runAction(() => api.post('/orgs/dissolve/veto', {}), 'Auflösung gestoppt.');
      return;
    case 'request-dissolution': {
      const isSolo = pageData?.org?.memberCount === 1;
      const confirmText = isSolo
        ? 'Du bist das einzige Mitglied — die Auflösung passiert sofort, das Restguthaben der Kasse wird dir zurückgezahlt. Fortfahren?'
        : 'Die Organisation wirklich auflösen? Das läuft als Antrag mit Frist.';
      if (!confirm(confirmText)) return;
      void runAction(() => api.post('/orgs/dissolve', {}), isSolo ? 'Organisation sofort aufgelöst.' : 'Auflösung beantragt.', { membershipChanged: isSolo });
      return;
    }
    case 'save-matrix':
      void saveMatrix();
      return;
    case 'approve-payout':
      void runAction(() => api.post(`/orgs/payout-requests/${btn.dataset.requestId ?? ''}/approve`, {}), 'Antrag genehmigt.');
      return;
    case 'reject-payout':
      void runAction(() => api.post(`/orgs/payout-requests/${btn.dataset.requestId ?? ''}/reject`, {}), 'Antrag abgelehnt.');
      return;
    case 'accept-application':
      void runAction(() => api.post(`/orgs/applications/${btn.dataset.applicationId ?? ''}/accept`, {}), 'Bewerbung angenommen.');
      return;
    case 'reject-application':
      void runAction(() => api.post(`/orgs/applications/${btn.dataset.applicationId ?? ''}/reject`, {}), 'Bewerbung abgelehnt.');
      return;
    case 'withdraw-application':
      void runAction(() => api.post(`/orgs/applications/${btn.dataset.applicationId ?? ''}/withdraw`, {}), 'Bewerbung zurückgezogen.', { membershipChanged: true });
      return;
    case 'join-org':
      if (!confirm('Dieser Organisation jetzt beitreten?')) return;
      void runAction(() => api.post(`/orgs/directory/${btn.dataset.orgId ?? ''}/join`, {}), 'Beigetreten.', { membershipChanged: true, openTab: 'UEBERSICHT' });
      return;
    case 'open-apply':
      applyOpenOrgId = btn.dataset.orgId ?? null;
      renderContent();
      return;
    case 'close-apply':
      applyOpenOrgId = null;
      renderContent();
      return;
    case 'page-directory':
      directoryPage = Number(btn.dataset.page ?? '1');
      void reloadDirectory();
      return;
    case 'page-leaderboard':
      leaderboardPage = Number(btn.dataset.page ?? '1');
      void reloadDirectory();
      return;
    case 'leaderboard-sort': {
      const sort = btn.dataset.sort;
      if (sort === 'stufe' || sort === 'members' || sort === 'punkte') {
        leaderboardSort = sort;
        leaderboardPage = 1;
        void reloadDirectory();
      }
      return;
    }
    case 'zentrale-upgrade':
      void runAction(() => api.post('/orgs/zentrale/upgrade', {}), 'Ausbau der Zentrale gestartet.');
      return;
    case 'gelaende-ausbau': {
      const parcelId = btn.dataset.parcelId ?? '';
      if (!window.confirm('Parzelle erweitern? Die Kosten gehen sofort aus der Org-Kasse.')) return;
      void runAction(() => api.post(`/orgs/gelaende/parzellen/${parcelId}/ausbau`, {}), 'Flächen-Ausbau gestartet.');
      return;
    }
    case 'gelaende-bauauswahl': {
      const id = btn.dataset.parcelId ?? '';
      bauParzelleId = bauParzelleId === id ? null : id;
      renderContent();
      return;
    }
    case 'gebaeude-toggle': {
      const id = btn.dataset.gebaeudeId ?? '';
      if (offeneGebaeude.has(id)) offeneGebaeude.delete(id); else offeneGebaeude.add(id);
      renderContent();
      return;
    }
    case 'anlage-auswahl': {
      const id = btn.dataset.gebaeudeId ?? '';
      anlageGebaeudeId = anlageGebaeudeId === id ? null : id;
      renderContent();
      return;
    }
    case 'gebaeude-oeffnen-ziel':
      navigate(btn.dataset.ziel ?? '/organisation');
      return;
    case 'gebaeude-bauen': {
      const parcelId = btn.dataset.parcelId ?? '';
      const typ = btn.dataset.typ ?? '';
      if (!window.confirm('Gebäude bauen? Der Baupreis geht sofort aus der Org-Kasse.')) return;
      void runAction(async () => {
        const res = await api.post('/orgs/gebaeude', { parcelId, typ });
        bauParzelleId = null;
        return res;
      }, 'Gebäude gebaut — es startet auf Anlagenstufe 0.');
      return;
    }
    case 'anlage-kauf': {
      const id = btn.dataset.gebaeudeId ?? '';
      if (!window.confirm('Anlagenstufe kaufen? Die Kosten gehen sofort aus der Org-Kasse.')) return;
      void runAction(async () => {
        const res = await api.post(`/orgs/gebaeude/${id}/anlage/kauf`, {});
        anlageGebaeudeId = null;
        return res;
      }, 'Ausbau der Anlage gestartet.');
      return;
    }
    case 'anlage-kette': {
      const id = btn.dataset.gebaeudeId ?? '';
      if (!window.confirm('Kette für diese Anlage starten? Der Einsatz geht sofort aus der Org-Kasse.')) return;
      void runAction(async () => {
        const res = await api.post(`/orgs/gebaeude/${id}/anlage/kette`, {});
        anlageGebaeudeId = null;
        return res;
      }, 'Kette gestartet — sie läuft im Tab AUFTRÄGE.');
      return;
    }
    case 'gebaeude-abreissen': {
      const id = btn.dataset.gebaeudeId ?? '';
      if (!window.confirm('Gebäude abreißen? Die Anlagenstufe geht verloren, ein Teil des Baupreises geht zurück in die Kasse.')) return;
      void runAction(async () => {
        const res = await api.delete(`/orgs/gebaeude/${id}`);
        offeneGebaeude.delete(id);
        return res;
      }, 'Gebäude abgerissen.');
      return;
    }
    case 'org-fahrzeug-verkaufen': {
      const id = btn.dataset.fahrzeugId ?? '';
      if (!window.confirm('Org-Fahrzeug verkaufen? Der Erlös geht in die Kasse.')) return;
      void runAction(() => api.delete(`/orgs/fahrzeuge/${id}`), 'Fahrzeug verkauft — Erlös in der Kasse.');
      return;
    }
    case 'org-fahrzeug-kaution': {
      const id = btn.dataset.fahrzeugId ?? '';
      if (!window.confirm('Kaution aus der Org-Kasse zahlen?')) return;
      void runAction(() => api.post(`/orgs/fahrzeuge/${id}/kaution`, {}), 'Fahrzeug ausgelöst.');
      return;
    }
    case 'baum-invest':
      void runAction(() => api.post('/orgs/baum/invest', { zweig: btn.dataset.zweig ?? '' }), 'Punkt investiert.');
      return;
    case 'baum-respec':
      if (!confirm('Diesen Zweig wirklich zurücksetzen? Kostet Geld aus der Kasse und sperrt danach weitere Skill-Änderungen.')) return;
      void runAction(() => api.post('/orgs/baum/respec', { zweig: btn.dataset.zweig ?? '' }), 'Punkt zurückgesetzt.');
      return;
    case 'open-lager-deposit':
      void openLagerDepositDialog();
      return;
    case 'open-lager-withdraw':
      void openLagerWithdrawDialog();
      return;
    case 'open-lager-request':
      openLagerRequestDialog();
      return;
    case 'close-lager-dialog':
      lagerDialog = null;
      renderContent();
      return;
    case 'confirm-lager-deposit':
    case 'confirm-lager-withdraw':
    case 'confirm-lager-request':
      void submitLagerDialog();
      return;
    case 'lager-qty-dec':
      if (lagerDialog) { lagerDialog = { ...lagerDialog, quantity: Math.max(1, lagerDialog.quantity - 1) }; renderContent(); }
      return;
    case 'lager-qty-inc':
      if (lagerDialog) {
        lagerDialog = { ...lagerDialog, quantity: Math.min(lagerDialog.quantity + 1, lagerDialog.availableQty) };
        renderContent();
      }
      return;
    case 'lager-qty-max':
      if (lagerDialog) { lagerDialog = { ...lagerDialog, quantity: lagerDialog.availableQty }; renderContent(); }
      return;
    default:
      return;
  }
}

function handleSubmit(e: Event): void {
  const form = e.target as HTMLFormElement;
  const kind = form.dataset.form;
  if (!kind) return;
  e.preventDefault();
  const currency = form.dataset.currency ?? '';

  switch (kind) {
    case 'org-verkauf': {
      const kanal = fieldValue(form, 'kanal') as OrgVerkaufKanal;
      const quantity = Number(fieldValue(form, 'menge'));
      const itemKey = form.dataset.item ?? '';
      if (!Number.isInteger(quantity) || quantity < 1) { showToast('Ungültige Menge.', 'error'); return; }
      const hinweis = kanal === 'SCHWARZMARKT'
        ? 'Suche gestartet — schließ sie in deinem Schwarzmarkt-Tab ab, der Erlös geht in die Kasse.'
        : kanal === 'STRASSENHANDEL'
          ? 'An den Straßenhandel verkauft — der Erlös ist in der Kasse.'
          : 'An den Hehler gegeben — der Erlös kommt nach der Kommission in die Kasse.';
      void runAction(() => api.post('/orgs/verkauf', { kanal, itemKey, quantity }), hinweis);
      return;
    }
    case 'org-aufgabe-uebernehmen': {
      const aufgabeId = form.dataset.aufgabeId ?? '';
      let body: Record<string, string>;
      if (form.dataset.typ === 'TRANSPORT') {
        const [quellePropertyId = '', zielPropertyId = ''] = fieldValue(form, 'paar').split('|');
        // NT-ORG-09 N1 — ohne Auswahlfeld fährt die Aufgabe mit einem Org-Fahrzeug.
        const vehicleKey = fieldValue(form, 'fahrzeug');
        body = vehicleKey ? { quellePropertyId, zielPropertyId, vehicleKey } : { quellePropertyId, zielPropertyId };
      } else {
        body = { propertyId: fieldValue(form, 'objekt') };
      }
      void runAction(async () => {
        const res = await api.post(`/orgs/auftraege/aufgaben/${aufgabeId}/uebernehmen`, body);
        offenesAufgabeFormular = null;
        return res;
      }, 'Aufgabe übernommen — sie läuft jetzt auf deinem Auftragsbrett.');
      return;
    }
    case 'create-org': {
      const name = fieldValue(form, 'org-titel');
      const rangVorlage = fieldValue(form, 'rangVorlage');
      void runAction(() => api.post('/orgs', { name, rangVorlage }), 'Organisation gegründet.', { membershipChanged: true, openTab: 'UEBERSICHT' });
      return;
    }
    case 'invite': {
      const username = fieldValue(form, 'einladen-ziel').trim();
      // G2 — exakte Spielersuche vorab, damit der Spieler eine klare Meldung sieht.
      void runAction(async () => {
        const lookup = await api.get<{ found: boolean }>(`/orgs/player-lookup?username=${encodeURIComponent(username)}`);
        if (!lookup.found) throw new ApiError('ORG_PLAYER_NOT_FOUND', 'Spieler nicht gefunden.', {}, 404);
        return api.post('/orgs/invites', { username });
      }, 'Einladung verschickt.');
      return;
    }
    case 'org-fahrzeug-kaufen': {
      const vehicleKey = fieldValue(form, 'fahrzeug');
      if (!vehicleKey) { showToast('Fahrzeug wählen.', 'error'); return; }
      void runAction(() => api.post('/orgs/fahrzeuge', { vehicleKey }), 'Fahrzeug gekauft — steht in der Spedition.');
      return;
    }
    case 'gelaende-name': {
      const name = fieldValue(form, 'gelaende-titel').trim();
      void runAction(() => api.put('/orgs/gelaende/name', { name }), 'Gelände umbenannt.');
      return;
    }
    case 'gebaeude-ware': {
      const [itemType = '', itemKey = ''] = fieldValue(form, 'ware').split('|');
      const quantity = Number(fieldValue(form, 'menge'));
      if (!Number.isInteger(quantity) || quantity < 1) { showToast('Ungültige Menge.', 'error'); return; }
      const body = { richtung: form.dataset.richtung ?? '', vehicleKey: fieldValue(form, 'fahrzeug'), itemKey, itemType, quantity };
      void runAction(async () => {
        const res = await api.post(`/orgs/gebaeude/${form.dataset.gebaeudeId ?? ''}/ware`, body);
        lagerProps = null; // Fahrzeug ist jetzt unterwegs — beim Neuladen frische Verfügbarkeit holen
        return res;
      }, 'Transport gestartet.');
      return;
    }
    case 'rank-names': {
      const rangNamen = [0, 1, 2, 3, 4].map((i) => fieldValue(form, `rangname-${String(i)}`));
      void runAction(() => api.put('/orgs/rank-names', { rangNamen }), 'Rangnamen gespeichert.');
      return;
    }
    case 'kasse-deposit':
    case 'kasse-withdraw': {
      const amountCents = parseAmountToCents(fieldValue(form, 'betrag'));
      if (amountCents === null) { showToast('Ungültiger Betrag.', 'error'); return; }
      const isDeposit = kind === 'kasse-deposit';
      void runAction(
        () => api.post(isDeposit ? '/orgs/kasse/deposit' : '/orgs/kasse/withdraw', { amountCents, currency }),
        isDeposit ? 'Eingezahlt.' : 'Entnommen.',
      );
      return;
    }
    case 'payout-request': {
      const amountCents = parseAmountToCents(fieldValue(form, 'betrag'));
      const reason = fieldValue(form, 'begruendung').trim();
      if (amountCents === null) { showToast('Ungültiger Betrag.', 'error'); return; }
      void runAction(() => api.post('/orgs/payout-requests', { amountCents, currency, reason }), 'Antrag gestellt.');
      return;
    }
    case 'payout-approve-custom': {
      const amountCents = parseAmountToCents(fieldValue(form, 'betrag'));
      if (amountCents === null) { showToast('Ungültiger Betrag.', 'error'); return; }
      void runAction(() => api.post(`/orgs/payout-requests/${form.dataset.requestId ?? ''}/approve`, { amountCents }), 'Abweichender Betrag ausgezahlt.');
      return;
    }
    case 'bonus': {
      const targetPlayerId = fieldValue(form, 'empfaenger');
      const amountCents = parseAmountToCents(fieldValue(form, 'betrag'));
      const submitter = (e as SubmitEvent).submitter as HTMLButtonElement | null;
      const bonusCurrency = submitter?.dataset.bonusCurrency ?? 'NOX';
      if (!targetPlayerId) { showToast('Bitte einen Empfänger wählen.', 'error'); return; }
      if (amountCents === null) { showToast('Ungültiger Betrag.', 'error'); return; }
      void runAction(() => api.post(`/orgs/members/${targetPlayerId}/bonus`, { amountCents, currency: bonusCurrency }), 'Bonus ausgezahlt.');
      return;
    }
    case 'tribute-share': {
      // X1 — Eingabe in Prozent, die Umrechnung macht der Server an genau einer Stelle.
      const tributeSharePercent = Number(fieldValue(form, 'prozent').trim().replace(',', '.'));
      if (!Number.isFinite(tributeSharePercent) || tributeSharePercent < 0) { showToast('Bitte einen Prozentwert eingeben.', 'error'); return; }
      void runAction(() => api.put('/orgs/tribute-share', { tributeSharePercent }), 'Abgabensatz geändert.');
      return;
    }
    case 'aufstieg': {
      const aufstiegsBedingungen: Record<string, { typ: string; wert: number }> = {};
      for (const stufe of ['2', '3', '4', '5']) {
        const wert = Number(fieldValue(form, `wert-${stufe}`));
        aufstiegsBedingungen[stufe] = { typ: fieldValue(form, `typ-${stufe}`), wert: Number.isFinite(wert) ? wert : 0 };
      }
      const autoAufstiegMaxStufe = Number(fieldValue(form, 'autoAufstiegMaxStufe'));
      void runAction(() => api.put('/orgs/promotion-conditions', { aufstiegsBedingungen, autoAufstiegMaxStufe }), 'Aufstiegsbedingungen gespeichert.');
      return;
    }
    case 'settings': {
      const beitrittsart = fieldValue(form, 'beitrittsart');
      const beschreibung = fieldValue(form, 'beschreibung');
      void runAction(() => api.put('/orgs/settings', { beitrittsart, beschreibung }), 'Einstellungen gespeichert.');
      return;
    }
    case 'apply': {
      const message = fieldValue(form, 'bewerbung').trim();
      const orgId = form.dataset.orgId ?? '';
      void runAction(async () => {
        await api.post(`/orgs/directory/${orgId}/applications`, { message });
        applyOpenOrgId = null;
      }, 'Bewerbung gesendet.', { membershipChanged: true });
      return;
    }
    case 'directory-search': {
      const freiePlaetzeEl = form.elements.namedItem('freiePlaetze');
      const sort = fieldValue(form, 'sortierung');
      directoryFilter = {
        search: fieldValue(form, 'suche').trim(),
        sort: sort === 'stufe' || sort === 'alter' ? sort : 'members',
        freiePlaetze: freiePlaetzeEl instanceof HTMLInputElement && freiePlaetzeEl.checked,
      };
      directoryPage = 1;
      void reloadDirectory();
      return;
    }
    default:
      return;
  }
}

function handleChange(e: Event): void {
  const target = e.target as HTMLElement;

  // Direkte Mengen-Eingabe (feuert bei Blur/Enter) — gezielter Patch statt Re-Render.
  if (target.id === 'org-lager-qty-input' && lagerDialog) {
    const input = target as HTMLInputElement;
    const max = parseInt(input.dataset.orgQtyMax ?? String(lagerDialog.availableQty), 10);
    const raw = parseInt(input.value, 10);
    lagerDialog = { ...lagerDialog, quantity: Math.max(1, Math.min(max, isNaN(raw) ? 1 : raw)) };
    updateLagerQtyDisplay();
    return;
  }
  if (target.id === 'org-lager-property') { handleLagerPropertyChange((target as HTMLSelectElement).value); return; }
  if (target.id === 'org-lager-item') { handleLagerItemChange((target as HTMLSelectElement).value); return; }
  if (target.id === 'org-lager-vehicle' && lagerDialog) {
    lagerDialog = { ...lagerDialog, vehicleKey: (target as HTMLSelectElement).value };
    renderContent();
    return;
  }

  const select = target.closest<HTMLSelectElement>('select[data-action="assign-rank"]');
  if (!select) return;
  const playerId = select.dataset.playerId ?? '';
  const rank = Number(select.value);
  void runAction(() => api.post(`/orgs/members/${playerId}/rank`, { rank }), 'Rang geändert.');
}

function handleInput(e: Event): void {
  const target = e.target as HTMLElement;
  if (target.id !== 'org-lager-begruendung' || !lagerDialog) return;
  lagerDialog.begruendung = (target as HTMLTextAreaElement).value;
  const btn = document.querySelector<HTMLButtonElement>('[data-action="confirm-lager-request"]');
  if (btn) {
    btn.disabled = !lagerDialog.itemKey || lagerDialog.quantity < 1 || lagerDialog.begruendung.trim().length === 0 || lagerDialog.isSubmitting;
  }
}

async function saveMatrix(): Promise<void> {
  // Feste Zeilen (H6) tragen kein data-matrix-recht und gehen nie mit.
  const inputs = document.querySelectorAll<HTMLInputElement>('input[data-matrix-recht]');
  const matrix: Record<string, boolean[]> = {};
  for (const recht of ORG_RECHTE) matrix[recht] = [false, false, false, false, false];
  inputs.forEach((input) => {
    const recht = input.dataset.matrixRecht;
    const stufe = Number(input.dataset.matrixStufe);
    if (recht && matrix[recht]) matrix[recht][stufe] = input.checked;
  });
  await runAction(() => api.put('/orgs/matrix', { rechteMatrix: matrix }), 'Rechte-Matrix gespeichert.');
}

async function reloadDirectory(): Promise<void> {
  try {
    await loadDirectory();
  } catch (err) {
    showToast(errorMessage(err), 'error');
  }
  renderContent();
}

/** Nach einer Aktion: Übersicht (Header-Daten) + nur der aktive Tab neu laden. */
async function reloadActiveTab(): Promise<void> {
  try {
    pageData = await api.get<OrgPageData>('/orgs/me');
    if (!pageData.org) activeTab = 'VERZEICHNIS';
    await loadTabData(activeTab);
  } catch (err) {
    showToast(errorMessage(err), 'error');
  }
  renderContent();
}

async function runAction(
  fn: () => Promise<unknown>,
  successMessage: string,
  opts: { membershipChanged?: boolean; openTab?: OrgSubTab } = {},
): Promise<void> {
  try {
    await fn();
    showToast(successMessage, 'info');
  } catch (err) {
    showToast(errorMessage(err), 'error');
    return;
  }
  if (opts.openTab) {
    activeTab = opts.openTab;
    storeTab(opts.openTab);
  }
  if (opts.membershipChanged) {
    // Beitritt/Austritt ändert, welche Tabs es überhaupt gibt — alle Tab-Caches verwerfen.
    overviewLog = null; kasseTab = null; hierarchieTab = null; lagerTab = null; ausbauTab = null; gelaendeTab = null; auftraegeTab = null; produktionTab = null;
  }
  await reloadActiveTab();
}
